import { type JSX, type Ref, useImperativeHandle, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { JobEditor } from "./JobEditor";
import {
  type JobEditorDataProps,
  jobEditorDataSchema,
} from "./JobEditor.schema";
import { JobEditorDialog } from "./JobEditorDialog";

export type JobEditorHandle = {
  open: (jobId?: number | null) => void;
  hide: () => void;
};

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    "job-editor": {
      props: JobEditorDataProps;
      handle: JobEditorHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-job-editor": HTMLElement & JobEditorHandle;
  }
}

export function RWCJobEditor({
  ref,
  projectId,
  jobId: initialJobId,
  contained,
  open: initialOpen,
}: JobEditorDataProps & { ref?: Ref<JobEditorHandle> }): JSX.Element {
  const host = useIslandHost();

  const [jobId, setJobId] = useState<number | null>(initialJobId);

  const [open, setOpen] = useState(initialOpen);

  useImperativeHandle(
    ref,
    () => ({
      open: (next = null) => {
        setJobId(next ?? null);
        setOpen(true);
      },
      hide: () => setOpen(false),
    }),
    [],
  );

  function emit(type: string, detail: Record<string, unknown> = {}) {
    host?.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
  }

  const onSaved = (savedId: number) => emit("job:saved", { jobId: savedId });

  const onCancel = () => emit("job:cancel");

  if (contained) {
    return (
      <JobEditor
        projectId={projectId}
        jobId={jobId}
        onSaved={onSaved}
        onCancel={onCancel}
      />
    );
  }

  return (
    <JobEditorDialog
      open={open}
      onOpenChange={setOpen}
      projectId={projectId}
      jobId={jobId}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  );
}

componentManager.register(
  rwc("job-editor", {
    component: RWCJobEditor,
    dataSchema: jobEditorDataSchema,
  }),
);
